import { boundClass } from 'autobind-decorator';
import { Satisfaction } from 'lib-shared-legacy-types';

import { DixaMessengerCSAT } from 'components/settings/components/sections/satisfaction/components/DixaMessengerCSAT/page/types';
import { EmailCSAT } from 'components/settings/components/sections/satisfaction/components/EmailCSAT/EmailPage/types';

import { ajaxSend } from '../../../api/api';

@boundClass
class CsatApi {
  private loadSettings(params: {
    level: Satisfaction.CSATSettingsLevel;
    channel: Satisfaction.CSATChannelsLevel;
  }): Promise<Satisfaction.CsatSettingsGetResponse> {
    return ajaxSend({
      path: '/v2/ratings/settings',
      method: 'GET',
      params: { level: params.level, channel: params.channel },
    });
  }

  /**
   * Here we are trying to fetch settings for all levels but the reason why we
   * can't make it one api call is that this.loadContactLevelSettings is not a
   * simple api call at this moment. to find out why read the comments on
   * loadContactLevelSettings method. When - in the future - the limitations of
   * contact level settings api are lifted this api call can be replaced with
   * simply calling this.loadSettings().
   */
  public async loadSettingForAllLevels(
    channel: Satisfaction.CSATChannelsLevel,
  ) {
    const [globalSettings, contactLevelSettings, languageLevelSettings] =
      await Promise.all([
        this.loadGlobalSettings(channel),
        this.loadContactLevelSettings(channel),
        this.loadLanguageLevelSettings(channel),
      ]);

    return {
      globalSettings,
      contactLevelSettings,
      languageLevelSettings,
    };
  }

  //
  // ─── GLOBAL SETTINGS ────────────────────────────────────────────────────────────
  //

  initializeCsat(
    channel: Satisfaction.CSATChannelsLevel,
  ): Promise<Satisfaction.EmailCsatSettingsGetResponse> {
    return ajaxSend({
      path: '/v2/ratings/settings/initialize',
      method: 'POST',
      data: {
        channel,
      },
    });
  }

  loadGlobalSettings(
    channel: Satisfaction.CSATChannelsLevel,
  ): Promise<Satisfaction.EmailCsatSettingsGetResponse> {
    return this.loadSettings({ level: 'channel', channel });
  }

  saveGlobalSettings(
    data:
      | Satisfaction.EmailCsatSettings
      | Satisfaction.CreateEmailCsatSettingsParams
      | Satisfaction.ChatCsatSettings
      | Satisfaction.CreateChatCsatSettingsParams
      | Satisfaction.DixaMessengerCsatSettings
      | Satisfaction.CreateDixaMessengerCsatSettingsParams,
    meta?: { edit: true },
  ): Promise<
    | Satisfaction.EmailCsatSettingsPostResponse
    | Satisfaction.ChatCsatSettingsPostResponse
    | Satisfaction.DixaMessengerCsatSettingsPostResponse
  > {
    if (meta?.edit) {
      return this.updateGlobalSettings(data as Satisfaction.EmailCsatSettings);
    }
    return this.createGlobalSettings(data);
  }

  createGlobalSettings(
    data:
      | Satisfaction.CreateEmailCsatSettingsParams
      | Satisfaction.CreateChatCsatSettingsParams
      | Satisfaction.CreateDixaMessengerCsatSettingsParams,
  ) {
    return ajaxSend<
      | Satisfaction.EmailCsatSettingsPostResponse
      | Satisfaction.ChatCsatSettingsPostResponse
      | Satisfaction.DixaMessengerCsatSettingsPostResponse
    >({
      path: '/v2/ratings/settings',
      method: 'POST',
      data,
    });
  }

  updateGlobalSettings(
    data:
      | Satisfaction.UpdateEmailCsatSettingsParams
      | Satisfaction.UpdateChatCsatSettingsParams
      | Satisfaction.UpdateDixaMessengerCsatSettingsParams,
  ) {
    return ajaxSend<
      | Satisfaction.EmailCsatSettingsPostResponse
      | Satisfaction.ChatCsatSettingsPostResponse
      | Satisfaction.DixaMessengerCsatSettingsPostResponse
    >({
      path: `/v2/ratings/settings/${data.id}`,
      method: 'PUT',
      data,
    });
  }

  //
  // ─── CONTACT LEVEL SETTINGS ─────────────────────────────────────────────────────
  //

  /**
   * In the MVP version of the csat for email we are not allowing for dedicated
   * settings for each contact point (email). but this capability already exists
   * in our backend. In the MVP we have only ONE contact-point-level settings,
   * and just link all the contact points to that settings. In the future we can
   * have as many contact-point-level settings as we want. but to make future
   * development easier here we "simulate" having multiple contact-level
   * settings by fetching the list of linked contact points to the only
   * contact-level-setting there is, and just copy the contact-level-setting
   * into the list of emails.
   *
   * Only email and dixa messenger have contact level settings
   */
  async loadContactLevelSettings(
    channel: Satisfaction.CSATChannelsLevel,
  ): Promise<
    | Satisfaction.EmailCsatSettingsEnrichedGetResponse
    | Satisfaction.ChatCsatSettingsEnrichedGetResponse
    | Satisfaction.DixaMessengerCsatSettingsEnrichedGetResponse
  > {
    try {
      const masterContactLevelSetting =
        await this.loadTheMasterContactLevelSetting(channel);

      const response = await this.loadContactLevelSettingsLinks(
        masterContactLevelSetting.id,
      );

      // enrich each setting with the contactPoint field
      const contactPointSettings = response.data.contact_points.map(
        (contactPoint) => ({
          ...masterContactLevelSetting,
          contactPoint: contactPoint,
        }),
      );

      return {
        channel,
        data: {
          rating_settings: contactPointSettings,
        },
        meta: { next_page: null, prev_page: null },
      };
    } catch (error) {
      return {
        channel,
        data: { rating_settings: [] },
        meta: { next_page: null, prev_page: null },
      };
    }
  }

  async loadTheMasterContactLevelSetting(
    channel: Satisfaction.CSATChannelsLevel,
  ): Promise<Satisfaction.EmailCsatSettings> {
    // load the contact level settings and since there is only one setting for all of the contact points
    // for now (it will probably change in the future) we assume that the first setting is THE setting.
    const {
      data: {
        rating_settings: [parentSettings],
      },
    } = await this.loadSettings({ level: 'contact_point', channel });

    if (!parentSettings) {
      return Promise.reject(
        new Error('There was no contact-level-settings defined'),
      );
    }

    return parentSettings;
  }

  // Only email and dixa messenger have contact level settings
  /* eslint-disable consistent-return */
  async saveContactLevelSettings(
    payload:
      | Omit<EmailCSAT.EmailAccountStatusChange, 'settings'>
      | Omit<DixaMessengerCSAT.MessengerStatusChange, 'settings'>,
    parentSetting:
      | Satisfaction.EmailCsatSettings
      | Satisfaction.DixaMessengerCsatSettings
      | null,
    channel: Satisfaction.CSATChannelsLevel,
  ): Promise<
    | Satisfaction.EmailCsatSettingsEnrichedGetResponse
    | Satisfaction.DixaMessengerCsatSettingsEnrichedGetResponse
  > {
    // In the future we can have independent settings for each contact point.
    // but for now we have only one settings for all of them
    // each contact point is either linked to that setting or not.

    // For now there is no way to save settings at the contact point level for chat
    if (channel === 'widgetchat') {
      throw new Error('"widgetchat" does not support contact-level settings ');
    }

    const contactPointIdentifier =
      channel === 'email'
        ? (payload as EmailCSAT.EmailAccountStatusChange).emailAddress
        : (payload as DixaMessengerCSAT.MessengerStatusChange).id;

    try {
      // Only want to link contact point if there is an identifier.
      if (payload.newStatus === true) {
        if (contactPointIdentifier) {
          await this.linkContactPointToSetting(
            parentSetting,
            contactPointIdentifier,
            channel,
          );
        }
        return this.loadContactLevelSettings(channel);
      }

      if (payload.newStatus === false) {
        // Only want to unlink contact point if there is an identifier
        if (contactPointIdentifier) {
          await this.unlinkContactPointFromSetting(
            parentSetting,
            contactPointIdentifier,
          );
        }
        return this.loadContactLevelSettings(channel);
      }

      // the new state was not true or false so it's not clear what is expected.
      console.error('Bad input', payload, parentSetting);
      throw new Error(
        'CsatApi.saveContactLevelSettings received a payload that is not acceptable',
      );
    } catch (error) {
      return Promise.reject(error);
    }
  }

  //
  // ─── LINKS BETWEEN SETTING AND CONTACT POINT ────────────────────────────────────
  //

  loadContactLevelSettingsLinks(
    contactLevelSettingId: string,
  ): Promise<Satisfaction.ContactPointLinkedToSettingResponse> {
    return ajaxSend({
      path: `/v2/ratings/settings/${contactLevelSettingId}/links`,
      method: 'GET',
    });
  }

  saveContactLevelSettingsLinks(
    contactLevelSettingId: string,
    contactPoint: string,
    operation: 'ADD_LINK' | 'DELETE_LINK',
  ): Promise<Satisfaction.ContactPointLinkedToSettingResponse> {
    return ajaxSend({
      path: `/v2/ratings/settings/${contactLevelSettingId}/links`,
      method: operation === 'DELETE_LINK' ? 'DELETE' : 'POST',
      data: {
        contact_point: contactPoint,
      },
    });
  }

  async linkContactPointToSetting(
    setting:
      | Satisfaction.EmailCsatSettings
      | Satisfaction.DixaMessengerCsatSettings
      | null,
    contactPointIdentifier: string,
    channel: Satisfaction.CSATChannelsLevel,
  ): Promise<void> {
    // we need to link this contact point to the only existing contact-point-level setting.
    if (setting?.id) {
      // it is already linked to a THE setting.
      return Promise.reject();
    }

    // since there's no parent setting, we use the master settings.
    try {
      setting = await this.loadTheMasterContactLevelSetting(channel);
    } catch (error) {
      console.error('failed to link email, because no setting is defined');
      return Promise.reject(error);
    }

    await this.saveContactLevelSettingsLinks(
      setting.id,
      contactPointIdentifier,
      'ADD_LINK',
    );

    return Promise.resolve();
  }

  async unlinkContactPointFromSetting(
    setting: Satisfaction.EmailCsatSettings | null,
    contactPointIdentifier: string,
  ): Promise<void> {
    // trying to unlink a contact point from a setting
    if (!setting?.id) {
      // it's not liked to a setting already! so we don't have to do anything
      return Promise.reject();
    }

    // save the new list of contact points to be linked with the parent settings.
    await this.saveContactLevelSettingsLinks(
      setting.id,
      contactPointIdentifier,
      'DELETE_LINK',
    );
    return Promise.resolve();
  }

  //
  // ─── LANGUAGE LEVEL SETTINGS ────────────────────────────────────────────────────
  //

  loadLanguageLevelSettings(channel: Satisfaction.CSATChannelsLevel) {
    return this.loadSettings({ level: 'language', channel });
  }

  async saveLanguageLevelSettings(
    settings:
      | Pick<Satisfaction.EmailCsatSettings, 'id' | 'enabled' | 'channel'>
      | Pick<Satisfaction.ChatCsatSettings, 'id' | 'enabled' | 'channel'>
      | Pick<
          Satisfaction.DixaMessengerCsatSettings,
          'id' | 'enabled' | 'channel'
        >,
  ): Promise<
    | Satisfaction.EmailCsatSettingsPostResponse
    | Satisfaction.ChatCsatSettingsPostResponse
    | Satisfaction.DixaMessengerCsatSettingsPostResponse
  > {
    if (settings.id) {
      // There already is a setting for this language, so we just update it.
      return ajaxSend({
        path: `/v2/ratings/settings/${settings.id}`,
        method: 'PUT',
        data: settings,
      });
    }

    /**
     * We need to create new language level setting. but it can't be done
     * without having a parent setting due to the hierarchical design of the
     * settings in the backend. for now, we have only ONE email-level setting
     * instance that all emails use (MVP version). So we have to choose that one
     * as the parent setting for all languages-level settings.
     *
     * It is likely that in the future each email will have it's own setting and
     * naturally a it's own language settings as it's child settings.
     *
     * Note that child settings override parent settings and parent settings act
     * as the fallback for child setting.
     */
    if (settings.channel) {
      const parentSettings = await this.loadTheMasterContactLevelSetting(
        settings.channel,
      );

      return ajaxSend({
        path: `/v2/ratings/settings`,
        method: 'POST',
        data: {
          ...settings,
          parent_id: parentSettings.id,
        },
      });
    }
    return Promise.reject();
  }
}

export const csatApi = new CsatApi();
